import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Rating,
  CircularProgress,
  Switch,
} from '@mui/material';
import { theme } from '../../../theme';
import ReviewService from '../../../services/review.service';

const SalonReviews = ({ salonId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    const loadReviews = async () => {
      if (!salonId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await ReviewService.getSalonReviews(salonId);
        const items = Array.isArray(data) ? data : data?.results || [];
        setReviews(items);
      } catch (err) {
        console.error('Error loading salon reviews in superadmin:', err);
        setError("Impossible de charger les avis pour ce salon.");
      } finally {
        setLoading(false);
      }
    };

    loadReviews();
  }, [salonId]);

  const hasReviews = reviews && reviews.length > 0;
  const visibleReviews = hasReviews ? reviews.filter((r) => !r?.is_hidden) : [];
  const averageRating = visibleReviews.length > 0
    ? visibleReviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / visibleReviews.length
    : null;

  const handleToggleHidden = async (review) => {
    if (!review?.review_id) return;
    setTogglingId(review.review_id);
    try {
      const res = await ReviewService.toggleHidden(review.review_id);
      if (typeof res?.is_hidden === 'boolean') {
        setReviews((prev) => prev.map((r) => (r.review_id === review.review_id ? { ...r, is_hidden: res.is_hidden } : r)));
      }
    } catch (err) {
      console.error('Error toggling review hidden state:', err);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: theme.borderRadius.lg,
        border: `1px solid ${theme.colors.border.light}`,
        boxShadow: theme.boxShadow.sm,
      }}
    >
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Avis des clients
          </Typography>
        }
        subheader={
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
            Vue d'ensemble des avis laissés par les clients pour ce salon.
          </Typography>
        }
      />
      <CardContent>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
              Chargement des avis...
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}

        {!loading && !error && !hasReviews && (
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
            Aucun avis n'a encore été laissé pour ce salon.
          </Typography>
        )}

        {!loading && !error && hasReviews && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Rating
                name="salon-rating-superadmin"
                value={averageRating}
                precision={0.5}
                readOnly
                size="small"
              />
              <Typography
                variant="body2"
                sx={{ ml: 1, color: theme.colors.text.secondary }}
              >
                {averageRating.toFixed(1)} / 5 · {visibleReviews.length} avis
              </Typography>
            </Box>

            <List dense sx={{ p: 0 }}>
              {reviews.slice(0, 10).map((review) => (
                <ListItem
                  key={review.review_id}
                  alignItems="flex-start"
                  disableGutters
                  sx={{ pb: 1.5, opacity: review.is_hidden ? 0.6 : 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">
                          {review.client_name || 'Client'}
                        </Typography>
                        <Rating
                          value={review.rating}
                          readOnly
                          size="small"
                        />
                        <Box sx={{ flex: 1 }} />
                        <Switch
                          checked={Boolean(review.is_hidden)}
                          onChange={() => handleToggleHidden(review)}
                          disabled={togglingId === review.review_id}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        {review.comment && (
                          <Typography
                            variant="body2"
                            sx={{ color: theme.colors.text.secondary }}
                          >
                            {review.comment}
                          </Typography>
                        )}
                        {review.created_at && (
                          <Typography
                            variant="caption"
                            sx={{ color: theme.colors.grey[600] }}
                          >
                            {new Date(review.created_at).toLocaleDateString('fr-FR')}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SalonReviews;
